import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { deliveryClient } from '../../../lib/connect/clients';
import { cn } from '../../../lib/utils';
import { BURDEN_LABELS, RATE_TYPE_LABELS } from './hooks';
import { periodDatesLabel, rateAssumptionNotes } from './rateApplicability';
const TYPES = {
    ...RATE_TYPE_LABELS,
    FLOOR: 'Floor (minimum)',
    AWARDED: 'Awarded'
};
const UNITS = [
    'HOURLY',
    'DAILY',
    'MONTHLY',
    'ANNUAL',
    'FIXED',
    'ITERATION'
];
const money = (r)=>`${r.currency && r.currency !== 'USD' ? `${r.currency} ` : '$'}${r.amount.toFixed(2)}/${r.rateUnit.toLowerCase().replace('hourly', 'hr')}`;
const when = (t)=>t ? t.toDate().toLocaleString() : 'time not recorded';
// The sheet and cell the parser recorded as evidence, for the link text.
function exactSourceLabel(evidenceJson) {
    try {
        const e = JSON.parse(evidenceJson);
        const where = [
            e.sheet && `sheet ${e.sheet}`,
            e.cell_ref && `cell ${e.cell_ref}`
        ].filter(Boolean);
        return where.length ? ` — ${where.join(', ')}` : '';
    } catch  {
        return '';
    }
}
function Picker({ label, value, options, onChange }) {
    return /*#__PURE__*/ _jsxs(Select, {
        value: value || '__none__',
        onValueChange: (v)=>onChange(v === '__none__' ? '' : v),
        children: [
            /*#__PURE__*/ _jsx(SelectTrigger, {
                "aria-label": label,
                className: "h-9 text-xs",
                children: /*#__PURE__*/ _jsx(SelectValue, {})
            }),
            /*#__PURE__*/ _jsxs(SelectContent, {
                children: [
                    /*#__PURE__*/ _jsxs(SelectItem, {
                        value: "__none__",
                        children: [
                            label,
                            ": not stated"
                        ]
                    }),
                    options.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                            value: o.id,
                            children: o.name
                        }, o.id))
                ]
            })
        ]
    });
}
// Uses the shared browser client: reasons and replacement metadata are absent
// from the legacy mutation hooks. Authorization still belongs to Ryugu.
export function RateObservationHistory({ companyId, rates, canEdit, entries, companies, vehicles }) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null);
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [replace, setReplace] = useState(false);
    const [entryId, setEntryId] = useState('');
    const [profileId, setProfileId] = useState('');
    const [vehicleId, setVehicleId] = useState('');
    const [type, setType] = useState('CEILING');
    const [unit, setUnit] = useState('HOURLY');
    const [burden, setBurden] = useState('UNKNOWN');
    const [period, setPeriod] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [worksite, setWorksite] = useState('');
    const [locality, setLocality] = useState('');
    const [documentId, setDocumentId] = useState('');
    const [sourceNote, setSourceNote] = useState('');
    const write = useMutation({
        mutationFn: async ()=>{
            if (!canEdit || !editing || !reason.trim()) throw new Error('A reason and registry authority are required.');
            if (editing.mode === 'correct') {
                return deliveryClient.correctRateObservation({
                    companyId,
                    correctsId: editing.id,
                    amount: Number(amount),
                    reason: reason.trim()
                });
            }
            return deliveryClient.deleteRateObservation({
                companyId,
                id: editing.id,
                reason: reason.trim(),
                replacement: replace ? {
                    companyId,
                    lcatEntryId: entryId,
                    companyProfileId: profileId,
                    contractVehicleId: vehicleId,
                    observationType: type,
                    amount: Number(amount),
                    rateUnit: unit,
                    burdenBasis: burden,
                    periodLabel: period,
                    periodStart: start ? Timestamp.fromDate(new Date(`${start}T00:00:00Z`)) : undefined,
                    periodEnd: end ? Timestamp.fromDate(new Date(`${end}T00:00:00Z`)) : undefined,
                    worksite,
                    locality,
                    sourceDocumentId: documentId,
                    evidenceJson: JSON.stringify({
                        note: sourceNote
                    })
                } : undefined
            });
        },
        onSuccess: async ()=>{
            await qc.invalidateQueries({
                queryKey: [
                    'rate observations'
                ]
            });
            setEditing(null);
        }
    });
    const superseded = new Set(rates.map((r)=>r.correctsId).filter(Boolean));
    const ordered = [];
    const visited = new Set();
    const append = (r)=>{
        if (visited.has(r.id)) return;
        visited.add(r.id);
        const original = rates.find((o)=>o.id === r.correctsId);
        if (original) append(original);
        ordered.push(r);
    };
    rates.filter((r)=>r.observationType !== 'PAY').forEach(append);
    const validAmount = amount.trim() !== '' && Number.isFinite(Number(amount)) && Number(amount) > 0;
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-3",
        children: ordered.map((r)=>{
            const inactive = superseded.has(r.id) || !!r.invalidatedAt || !!r.invalidationReason;
            const activeEditor = editing?.id === r.id && canEdit && !inactive;
            return /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.deliveryRegistries.lcatRateRow,
                className: cn('rounded-md border p-3 text-xs space-y-2', inactive ? 'border-border/60 text-muted-foreground' : 'border-primary/40'),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: inactive ? 'neutral' : 'success',
                                        children: TYPES[r.observationType] ?? r.observationType
                                    }),
                                    ' ',
                                    /*#__PURE__*/ _jsx("strong", {
                                        className: cn(inactive && 'line-through'),
                                        children: money(r)
                                    }),
                                    ' ',
                                    !inactive && (r.correctsId ? 'Current valid correction' : 'Current observation')
                                ]
                            }),
                            canEdit && !inactive && /*#__PURE__*/ _jsx("span", {
                                className: "flex gap-1",
                                children: [
                                    'correct',
                                    'invalidate'
                                ].map((mode)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: mode === 'invalidate' ? 'destructive' : 'ghost',
                                        "data-testid": mode === 'correct' ? Testids.deliveryRegistries.lcatRateCorrect : Testids.deliveryRegistries.lcatRateInvalidate,
                                        disabled: write.isPending,
                                        onClick: ()=>{
                                            write.reset();
                                            setEditing({
                                                id: r.id,
                                                mode
                                            });
                                            setAmount(r.amount.toFixed(2));
                                            setReason('');
                                            setReplace(false);
                                            setEntryId('');
                                            setProfileId('');
                                            setVehicleId('');
                                            setType('CEILING');
                                            setUnit('HOURLY');
                                            setBurden('UNKNOWN');
                                            setPeriod('');
                                            setStart('');
                                            setEnd('');
                                            setWorksite('');
                                            setLocality('');
                                            setDocumentId('');
                                            setSourceNote('');
                                        },
                                        children: mode === 'correct' ? 'Correct' : 'Invalidate'
                                    }, mode))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        children: [
                            r.lcatTitle,
                            r.companyName || 'no company attribution recorded',
                            r.vehicleName || 'no vehicle recorded',
                            r.periodLabel || 'no period stated',
                            BURDEN_LABELS[r.burdenBasis] ?? r.burdenBasis,
                            periodDatesLabel(r),
                            r.worksite,
                            r.locality,
                            ...rateAssumptionNotes(r)
                        ].filter(Boolean).join(' · ')
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            "Recorded by ",
                            r.recordedBy || 'actor not recorded',
                            " · ",
                            when(r.createdAt)
                        ]
                    }),
                    superseded.has(r.id) && /*#__PURE__*/ _jsx("div", {
                        children: "Superseded by a correction — original evidence kept unchanged."
                    }),
                    r.correctsId && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            "Corrects the struck figure above · Corrects observation ",
                            r.correctsId,
                            " ·",
                            ' ',
                            r.correctionReason || 'reason not returned',
                            " · by",
                            ' ',
                            r.correctedById || 'actor not recorded',
                            " · ",
                            when(r.createdAt)
                        ]
                    }),
                    (r.invalidatedAt || r.invalidationReason) && /*#__PURE__*/ _jsxs("div", {
                        children: [
                            "Invalidated: ",
                            r.invalidationReason || 'reason not returned',
                            " · by",
                            ' ',
                            r.invalidatedBy || 'actor not recorded',
                            " · ",
                            when(r.invalidatedAt)
                        ]
                    }),
                    (r.replacementId || r.replacesId) && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatRateRelation,
                        children: [
                            r.replacementId ? `Replaced by observation ${r.replacementId}` : `Replaces invalidated observation ${r.replacesId}`,
                            ". This is an independently attributed observation, not a correction of the original identity or type. Open its labor category to inspect it."
                        ]
                    }),
                    r.sourceChangeSetId && r.sourceChangeItemId && /*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsxs(Link, {
                            "data-testid": Testids.deliveryRegistries.lcatRateSourceLink,
                            className: "underline",
                            to: `/registries/pricing-sheets/${r.sourceChangeSetId}?item=${r.sourceChangeItemId}`,
                            children: [
                                "View exact source",
                                exactSourceLabel(r.evidenceJson)
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("details", {
                        "data-testid": Testids.deliveryRegistries.lcatRateEvidence,
                        children: [
                            /*#__PURE__*/ _jsx("summary", {
                                children: "Original source and evidence"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    "Observation ",
                                    r.id,
                                    " · Labor category version ",
                                    r.lcatEntryId,
                                    " · Source:",
                                    ' ',
                                    r.source || 'not recorded'
                                ]
                            }),
                            /*#__PURE__*/ _jsx("pre", {
                                className: "whitespace-pre-wrap break-words",
                                children: r.evidenceJson || 'No source evidence recorded.'
                            })
                        ]
                    }),
                    activeEditor && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 border-t pt-2",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                children: editing.mode === 'correct' ? 'Append a corrected amount. Original type, identity, source and applicability stay unchanged.' : 'Invalidate this observation with a reason. Its original amount and evidence stay in history; existing proposal selections and pricing sheets do not change.'
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                "aria-label": editing.mode === 'correct' ? 'Correction reason' : 'Invalidation reason',
                                "data-testid": Testids.deliveryRegistries.lcatRateReason,
                                value: reason,
                                onChange: (e)=>setReason(e.target.value),
                                placeholder: "Reason (required)"
                            }),
                            editing.mode === 'invalidate' && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                label: "Create a correctly attributed replacement",
                                controlProps: {
                                    checked: replace,
                                    onCheckedChange: (v)=>setReplace(v === true)
                                }
                            }),
                            editing.mode === 'invalidate' && replace && /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-2 gap-2",
                                "data-testid": Testids.deliveryRegistries.lcatRateReplacement,
                                children: [
                                    /*#__PURE__*/ _jsx(Picker, {
                                        label: "Replacement labor category",
                                        value: entryId,
                                        options: entries.map((e)=>({
                                                id: e.id,
                                                name: `${e.title} — ${e.companyName || 'no company'} — ${e.contractVehicleName || 'no vehicle'}`
                                            })),
                                        onChange: setEntryId
                                    }),
                                    /*#__PURE__*/ _jsx(Picker, {
                                        label: "Replacement company",
                                        value: profileId,
                                        options: companies,
                                        onChange: setProfileId
                                    }),
                                    /*#__PURE__*/ _jsx(Picker, {
                                        label: "Replacement vehicle",
                                        value: vehicleId,
                                        options: vehicles,
                                        onChange: setVehicleId
                                    }),
                                    /*#__PURE__*/ _jsx(Picker, {
                                        label: "Replacement type",
                                        value: type,
                                        options: Object.entries(TYPES).map(([id, name])=>({
                                                id,
                                                name
                                            })),
                                        onChange: (v)=>{
                                            setType(v);
                                            if (unit === 'FIXED' && [
                                                'CEILING',
                                                'BILLED',
                                                'INFERRED',
                                                'FLOOR',
                                                'AWARDED'
                                            ].includes(v)) setUnit('HOURLY');
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Picker, {
                                        label: "Replacement unit",
                                        value: unit,
                                        options: UNITS.filter((u)=>u !== 'FIXED' || ![
                                                'CEILING',
                                                'BILLED',
                                                'INFERRED',
                                                'FLOOR',
                                                'AWARDED'
                                            ].includes(type)).map((id)=>({
                                                id,
                                                name: id
                                            })),
                                        onChange: setUnit
                                    }),
                                    /*#__PURE__*/ _jsx(Picker, {
                                        label: "Replacement burden",
                                        value: burden,
                                        options: Object.entries(BURDEN_LABELS).map(([id, name])=>({
                                                id,
                                                name
                                            })),
                                        onChange: setBurden
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement period",
                                        placeholder: "Period as written",
                                        value: period,
                                        onChange: (e)=>setPeriod(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement period start",
                                        type: "date",
                                        value: start,
                                        onChange: (e)=>setStart(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement period end",
                                        type: "date",
                                        value: end,
                                        onChange: (e)=>setEnd(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement worksite",
                                        placeholder: "Worksite",
                                        value: worksite,
                                        onChange: (e)=>setWorksite(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement locality",
                                        placeholder: "Locality",
                                        value: locality,
                                        onChange: (e)=>setLocality(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement source document ID",
                                        placeholder: "Source document ID (if known)",
                                        value: documentId,
                                        onChange: (e)=>setDocumentId(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        "aria-label": "Replacement source note",
                                        placeholder: "Source evidence (required)",
                                        value: sourceNote,
                                        onChange: (e)=>setSourceNote(e.target.value)
                                    })
                                ]
                            }),
                            (editing.mode === 'correct' || replace) && /*#__PURE__*/ _jsx(SpecraInput, {
                                "aria-label": editing.mode === 'correct' ? 'Corrected amount' : 'Replacement amount',
                                inputMode: "decimal",
                                value: amount,
                                onChange: (e)=>setAmount(e.target.value)
                            }),
                            write.isError && /*#__PURE__*/ _jsx("p", {
                                role: "alert",
                                "data-testid": Testids.deliveryRegistries.lcatRateError,
                                children: write.error instanceof Error ? write.error.message : 'Unable to save. Please retry.'
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: editing.mode === 'invalidate' ? 'destructive' : 'default',
                                        "data-testid": editing.mode === 'correct' ? Testids.deliveryRegistries.lcatRateSaveCorrection : Testids.deliveryRegistries.lcatRateConfirmInvalidation,
                                        disabled: write.isPending || !reason.trim() || (editing.mode === 'correct' || replace) && !validAmount || replace && (!entryId || !type || !unit || !burden || !sourceNote.trim() || start !== '' && end !== '' && start > end),
                                        onClick: ()=>write.mutate(),
                                        children: write.isPending ? 'Saving…' : editing.mode === 'correct' ? 'Save correction' : 'Confirm invalidation'
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: write.isPending,
                                        onClick: ()=>{
                                            setEditing(null);
                                            write.reset();
                                        },
                                        children: "Cancel"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }, r.id);
        })
    });
}
